import * as React from 'react';

export type WindowSize = {
    width: number;
    height: number;
};

// use iPhone 11 as fallback for SSR
const DEFAULT_SSR_WINDOW_SIZE = {
    width: 414,
    height: 896,
};

// we will have hydration errors if render a different content on SSR
// than we do on the client
// thus we will always inintialize with SSR size and only
// _after_  we booted, we'll set the actual size
function getCurrentSize(forceSsr = false): WindowSize {
    if (forceSsr || typeof window === 'undefined') {
        return DEFAULT_SSR_WINDOW_SIZE;
    }

    // found that at least in Chrome after enabling and disabling
    // the "emulate device" feature, `window.innerX` doesn't
    // represent the proper value, thus we'll fallback to use
    // the smaller value of `inner` and `outer`
    return {
        width: Math.min(window.innerWidth, window.outerWidth),
        height: Math.min(window.innerHeight, window.outerHeight),
    };
}

export default function useWindowSize(): WindowSize {
    const [size, setSize] = React.useState(() => getCurrentSize(true));

    React.useEffect(() => {
        const handler = () => {
            setSize(getCurrentSize());
        };

        window.addEventListener('resize', handler);

        // we booted, so let's trigger the handler
        handler();

        return () => window.removeEventListener('resize', handler);
    }, []);

    return size;
}
